import { Course, ProcessedCourse } from "@/common/course/types";
import isCourseArchived from "./utils/isCourseArchived";
import setProcessedCourseOverrides from "./setProcessedCourseOverrides";
import { CourseRun } from "@/common/courseRun/types";
import { Instructor } from "@/common/instructor/types";

// note: 'reviewed' actually means 'scheduled' (past PC review and ready for publication on publication date)
export const COURSE_RUN_STATUS_REVIEWED = "reviewed";
export const COURSE_RUN_AVAILABILITY_ARCHIVED = "archived";

/**
 * Check if a course run is archived based on its availability field
 */
export const isCourseRunArchived = (courseRun: CourseRun): boolean => {
  if (courseRun.availability) {
    return (
      courseRun.availability.toLowerCase() === COURSE_RUN_AVAILABILITY_ARCHIVED
    );
  }
  return false;
};

/**
 * Hydrate course run instructors from course-level instructor data.
 * Course runs store only { uuid, sortValue } references to reduce Algolia size.
 * This function reconstructs full instructor data using the course-level instructors.
 */
const hydrateInstructors = (
  courseRun: CourseRun,
  instructorMap: Map<string, Omit<Instructor, "aboutMe">>,
): CourseRun => {
  // Check if instructors need hydration (they only have uuid/sortValue, not full data)
  const needsHydration =
    courseRun.instructors.length > 0 &&
    !("firstName" in courseRun.instructors[0]);

  if (!needsHydration) {
    return courseRun;
  }

  // Hydrate instructors from course-level data
  const hydratedInstructors: Omit<Instructor, "aboutMe">[] = [];

  for (const ref of courseRun.instructors) {
    const fullInstructor = instructorMap.get(ref.uuid);
    if (fullInstructor) {
      hydratedInstructors.push({
        ...fullInstructor,
        sortValue: ref.sortValue,
      });
    }
  }

  return {
    ...courseRun,
    instructors: hydratedInstructors,
  };
};

/**
 * Filter course runs to get active ones based on marketability, enrollability, and archived status.
 */
export const getActiveRuns = (
  allCourseRuns: CourseRun[],
  advertisedCourseRunUuid: string | null,
): CourseRun[] => {
  return allCourseRuns.filter(
    (courseRun) =>
      courseRun.isMarketable &&
      // The historical type check is a temporary measure to prevent those courses from reaching production;
      // These course runs should have `isMarketable: false` but there is a data mismatch causing them to resolve
      // to true
      // Exclude Historical course runs unless they contain "learn/ap/rice-university-ap-r" in marketingUrl
      (!courseRun.type.startsWith("Historical") ||
        courseRun.marketingUrl?.includes("learn/ap/rice-university-ap-r")) &&
      (courseRun.uuid === advertisedCourseRunUuid ||
        // Allow other course runs that are enrollable and not archived
        (courseRun.isEnrollable && !isCourseRunArchived(courseRun))),
  );
};

/**
 * Apply override logic to select the best active course run.
 * If the advertised run ends in less than 2 weeks or is not enrollable,
 * try to find a better alternative run.
 */
const getActiveCourseRunWithOverride = (
  activeRuns: CourseRun[],
  advertisedCourseRunUuid: string | null,
): CourseRun | null => {
  // Initially try to use the advertised run
  let activeCourseRun =
    activeRuns.find(
      (courseRun) => courseRun.uuid === advertisedCourseRunUuid,
    ) || null;

  // Apply override logic if the run ends soon or is not enrollable
  if (activeCourseRun && activeCourseRun.end) {
    const now = new Date();
    const endDate = new Date(activeCourseRun.end);
    const twoWeeksFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

    if (endDate < twoWeeksFromNow || !activeCourseRun.isEnrollable) {
      // Find the next active run using preference logic
      const [nextRun] = activeRuns
        .filter(
          (run) =>
            run.start &&
            run.isEnrollable && // Ensure the alternative run is enrollable
            (!run.end || new Date(run.end) >= twoWeeksFromNow),
        )
        .sort((a, b) => {
          const startA = new Date(a.start!);
          const startB = new Date(b.start!);
          const endA = a.end ? new Date(a.end) : null;
          const endB = b.end ? new Date(b.end) : null;

          const hasStartedA = startA <= now;
          const hasStartedB = startB <= now;

          // Prefer runs that have already started over future runs
          if (hasStartedA && !hasStartedB) return -1;
          if (!hasStartedA && hasStartedB) return 1;

          // If both have started, prefer the one with end date further in the future
          if (hasStartedA && hasStartedB) {
            // Handle null end dates (treat as far future)
            if (!endA && !endB) return startA.getTime() - startB.getTime();
            if (!endA) return -1;
            if (!endB) return 1;
            return endB.getTime() - endA.getTime(); // Later end date first
          }

          // If both are future runs, prefer earlier start date
          return startA.getTime() - startB.getTime();
        });

      if (nextRun) {
        activeCourseRun = nextRun;
      }
    }
  }

  return activeCourseRun;
};

/**
 * Determines if a course should be visible on edX platform.
 * A course is visible if it's currently active OR if it has any course runs
 * (even archived ones) and is not deleted.
 */
export const shouldCourseBeVisibleOnEdx = (
  isActive: boolean,
  course: Course,
  courseRuns: CourseRun[],
): boolean => {
  return (
    isActive || !!(courseRuns && courseRuns.length > 0 && !course.isDeleted)
  );
};

const getProcessedCourseFromCourse = (
  course: Course,
  inPreviewMode: boolean = false,
) => {
  const UrlObject = URL;
  let advertisedCourseRunUuid = course.advertisedCourseRunUuid; // waiting for prop to be added to payload

  // Handle empty advertisedCourseRunUuid by selecting the first available course run
  if (
    !advertisedCourseRunUuid &&
    course.courseRuns &&
    course.courseRuns.length > 0
  ) {
    // Select the first course run as the advertised run if none is specified
    advertisedCourseRunUuid = course.courseRuns[0].uuid;
  }

  // Build instructor map from course-level instructors for hydration
  const instructorMap = new Map(
    (course.instructors || []).map((instructor) => [
      instructor.uuid,
      instructor,
    ]),
  );

  // Hydrate course runs with full instructor data from course-level instructors
  const hydratedCourseRuns = (course.courseRuns || []).map((courseRun) =>
    hydrateInstructors(courseRun, instructorMap),
  );

  const processedCourse: ProcessedCourse = course as ProcessedCourse;

  // Replace course runs with hydrated versions
  processedCourse.courseRuns = hydratedCourseRuns;

  processedCourse.activeCourseRun = null;
  processedCourse.activeCourseRuns = [];
  processedCourse.isActive = false;
  processedCourse.inEdx = true;
  processedCourse.subjectEn = course.subjectEn;
  // prune unused fields
  processedCourse.subjects =
    course.subjects
      ?.map((subject) => ({
        languageCode: subject.languageCode,
        name: subject.name,
        slug: subject.slug,
        sortValue: subject.sortValue,
        uuid: subject.uuid,
      }))
      .sort((a, b) => (a.sortValue ?? 0) - (b.sortValue ?? 0)) || [];
  // only relevant for search
  processedCourse._highlightResult = undefined;

  // if course_run_statuses = ['reviewed'], all course runs are either reviewed or unpublished,
  // so set the active course run to the first 'reviewed' run we find
  processedCourse.isPreview = course.courseRunStatuses?.every(
    ({ status }) => status.toLowerCase() === COURSE_RUN_STATUS_REVIEWED,
  );

  if (processedCourse.isPreview) {
    processedCourse.activeCourseRun =
      processedCourse.courseRuns.find(
        ({ status }) => status.toLowerCase() === COURSE_RUN_STATUS_REVIEWED,
      ) || null;
    processedCourse.activeCourseRuns = processedCourse.activeCourseRun
      ? [processedCourse.activeCourseRun]
      : [];
    processedCourse.isActive = inPreviewMode;
    processedCourse.inEdx = inPreviewMode;
  } else {
    // Use shared utility to get active runs (now with hydrated instructors)
    const activeRuns = getActiveRuns(
      processedCourse.courseRuns,
      advertisedCourseRunUuid,
    );

    processedCourse.activeCourseRuns = activeRuns.map((courseRun) => ({
      ...courseRun,
      marketingPath: new UrlObject(courseRun.marketingUrl).pathname,
    }));

    // Use shared utility to apply override logic and get the final active course run
    const activeCourseRunWithOverride = getActiveCourseRunWithOverride(
      activeRuns,
      advertisedCourseRunUuid,
    );

    // Find the corresponding run from the already-mapped activeCourseRuns
    processedCourse.activeCourseRun = activeCourseRunWithOverride
      ? processedCourse.activeCourseRuns.find(
          (run) => run.uuid === activeCourseRunWithOverride.uuid,
        ) || null
      : null;

    processedCourse.isActive = !!processedCourse.activeCourseRun;
    // Fix: inEdx should be true for active courses OR if the course has any course runs (even archived ones)
    // This ensures courses with archived runs are still accessible
    processedCourse.inEdx = shouldCourseBeVisibleOnEdx(
      processedCourse.isActive,
      course,
      processedCourse.activeCourseRuns,
    );
  }

  // OFAC: mark the course as restricted if any of its active course runs are restricted
  processedCourse.hasOfacRestrictions = processedCourse.activeCourseRuns.some(
    (run) => run.hasOfacRestrictions,
  );

  processedCourse.edxPath = processedCourse.productUrl
    ? new UrlObject(processedCourse.productUrl).pathname
    : "";

  processedCourse.additionalMetadata = course.additionalMetadata || [];

  processedCourse.onlyArchivedCourseRuns = processedCourse.courseRuns.every(
    (courseRun) =>
      courseRun.availability?.toLowerCase() ===
      COURSE_RUN_AVAILABILITY_ARCHIVED,
  );

  // TODO Process topics
  // LEGACY CODE FOR REFERENCE
  //   const topics = [];
  //   course.topics.forEach((topic) => {
  //     topics.push({ topic });
  //   });
  //   course.topics = topics;

  setProcessedCourseOverrides(processedCourse);

  return processedCourse;
};

export default getProcessedCourseFromCourse;
